import React, { useState } from 'react';
import axios from 'axios';
import { Lightbulb, Sparkles, TrendingUp, TrendingDown, AlertCircle } from 'lucide-react';
import ReactMarkdown from 'react-markdown';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function WheelInsights({ assessmentId, currentScores }) {
  const [insights, setInsights] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const generateInsights = async () => {
    if (!assessmentId) {
      setError('Please save your assessment first to generate insights');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(`${API_URL}/wheel-assessments/${assessmentId}/generate-insights`);
      setInsights(response.data.insights);
      setSummary(response.data.summary);
    } catch (err) {
      console.error('Error generating insights:', err);
      setError('Failed to generate insights. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!insights && !loading && !error) {
    return (
      <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-8 text-center">
        <div className="w-16 h-16 rounded-full bg-[var(--accent)]/20 flex items-center justify-center mx-auto mb-4">
          <Lightbulb className="w-8 h-8 text-[var(--accent)]" />
        </div>
        <h3 className="text-xl font-semibold text-white mb-2">Get AI Insights</h3>
        <p className="text-sm text-gray-400 mb-6 max-w-md mx-auto">
          Let our AI analyze your RMMBR Circle assessment and provide personalized insights, patterns, and actionable recommendations.
        </p>
        <button
          onClick={generateInsights}
          disabled={!assessmentId}
          className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white px-6 py-3 rounded-lg font-medium transition-all flex items-center gap-2 mx-auto disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Sparkles className="w-5 h-5" />
          Generate Insights
        </button>
        {!assessmentId && (
          <p className="text-xs text-gray-500 mt-3">Save your assessment first to enable insights</p>
        )}
      </div>
    );
  }

  if (loading) {
    return (
      <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-12 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[var(--accent)]/30 border-t-[var(--accent)] mx-auto mb-4"></div>
        <p className="text-gray-400">Analyzing your assessment...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/10 backdrop-blur-xl rounded-2xl border border-red-500/20 p-6">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-red-300 text-sm">{error}</p>
            <button
              onClick={generateInsights}
              className="text-red-400 hover:text-red-300 text-sm font-medium mt-2 underline"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-3 gap-4">
          {/* Average Score */}
          <div className="bg-gradient-to-br from-[#f5e6d3] to-[#ede0cf] rounded-xl p-4 border-l-4 border-[var(--accent)]">
            <p className="text-sm text-[#5a5248] font-medium mb-1">Overall Balance</p>
            <p className="text-3xl font-bold text-[var(--accent)]">{summary.averageScore}</p>
            <p className="text-xs text-gray-600 mt-1">out of 10</p>
          </div>

          {/* Strongest Areas */}
          <div className="bg-gradient-to-br from-[#d4edda] to-[#c3e6cb] rounded-xl p-4 border-l-4 border-green-500">
            <p className="text-sm text-green-900 font-medium mb-2 flex items-center gap-1">
              <TrendingUp className="w-4 h-4" />
              Strongest Areas
            </p>
            <div className="space-y-1">
              {summary.highestAreas.map((area, idx) => (
                <div key={idx} className="flex items-center justify-between text-xs">
                  <span className="text-green-800">{area.category}</span>
                  <span className="font-bold text-green-900">{area.score}/10</span>
                </div>
              ))}
            </div>
          </div>

          {/* Growth Opportunities */}
          <div className="bg-gradient-to-br from-[#fff3cd] to-[#ffecb5] rounded-xl p-4 border-l-4 border-yellow-500">
            <p className="text-sm text-yellow-900 font-medium mb-2 flex items-center gap-1">
              <TrendingDown className="w-4 h-4" />
              Growth Areas
            </p>
            <div className="space-y-1">
              {summary.lowestAreas.map((area, idx) => (
                <div key={idx} className="flex items-center justify-between text-xs">
                  <span className="text-yellow-800">{area.category}</span>
                  <span className="font-bold text-yellow-900">{area.score}/10</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* AI Insights */}
      <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-6">
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="w-5 h-5 text-[var(--accent)]" />
          <h3 className="text-xl font-semibold text-white">AI Analysis</h3>
        </div>

        <div className="prose prose-invert prose-sm max-w-none">
          <ReactMarkdown>
            {insights}
          </ReactMarkdown>
        </div>

        <div className="mt-6 pt-6 border-t border-white/10 flex justify-between items-center">
          <p className="text-xs text-gray-500">Generated by AI • Review for accuracy</p>
          <button
            onClick={generateInsights}
            className="text-[var(--accent)] hover:text-[var(--accent-hover)] text-sm font-medium flex items-center gap-1 transition-colors"
          >
            <Sparkles className="w-4 h-4" />
            Regenerate
          </button>
        </div>
      </div>
    </div>
  );
}

export default WheelInsights;
